// The standings table and the prize table, for the clubhouse screen at /e/:slug.
//
// Presentation only. Every number on this page comes from `@shared/sync/leaderboard`,
// which the phone app also renders from — see the note at the top of that file for why
// there is exactly one implementation of golf's ranking rules in this product.
//
// Two behaviours of that engine are surfaced here and are easy to get wrong:
//
//   • `position` is EMPTY for anyone yet to hole out. At a shotgun start the whole field
//     is on nothing for the first fifteen minutes, and a board declaring twenty-four teams
//     tied for first is worse than useless. Empty renders as a dash.
//   • `thru` is holes COMPLETED, not the hole number a group has reached. A group that
//     started on 5 and is stood on the 8th tee is "thru 3". The column says THRU for that
//     reason and must never be labelled HOLE.
//
// Sized for a television across a room first and a phone second, which is the opposite of
// the usual order and the reason the type scales with the viewport rather than sitting at
// a comfortable reading size.

import type { LeaderboardRow } from '@shared/sync/leaderboard';
import { fmtToPar } from '@shared/sync/leaderboard';
import { fmtMoney } from '@shared/sync/toDomain';
import { CONTEST_LABELS, CONTEST_SHORT } from '@shared/engine/contests';
import type { ContestKind } from '@shared/types';

export type ContestLine = {
  holeIdx: number;
  kind: string;
  label: string | null;
  winner: string | null;
};

/**
 * The page's own CSS.
 *
 * It lives in the component rather than `theme.css` because that stylesheet is the shared
 * house style for all three web surfaces and these rules are for this one screen. Every
 * colour is a variable from it — nothing new is invented here.
 */
export function BoardStyles() {
  return (
    <style>{`
/* Two classes so this beats .wrap's own max-width whatever order the sheets land in —
   a leaderboard wants the whole television, not a 1100px reading column. */
.wrap.lb-page { max-width: 1400px; }

/* ── Header ─────────────────────────────────────────────────────────────── */
.lb-head {
  display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between;
  gap: 12px 24px; margin-bottom: 20px;
}
.lb-title {
  margin: 0; font-size: clamp(28px, 4.4vw, 64px); line-height: 1.05;
  font-weight: 800; letter-spacing: -0.5px;
}
.lb-sub {
  margin: 8px 0 0; font-size: clamp(14px, 1.4vw, 22px);
  color: var(--text-dim); display: flex; flex-wrap: wrap; gap: 6px 10px; align-items: center;
}
.lb-dot { color: var(--brass); opacity: .7; }
.lb-chip {
  display: inline-block; padding: 3px 10px; border-radius: 999px;
  border: 1px solid var(--brass); color: var(--brass);
  font-size: clamp(11px, 1vw, 15px); font-weight: 700;
  text-transform: uppercase; letter-spacing: .6px; white-space: nowrap;
}
.lb-chip.live { background: var(--brass); color: #1C1400; }
.lb-chip.final { border-color: var(--text-dim); color: var(--text-dim); }
.lb-meta { text-align: right; font-size: clamp(12px, 1.05vw, 16px); color: var(--text-dim); }
.lb-pulse {
  display: inline-block; width: 8px; height: 8px; border-radius: 50%;
  background: var(--ok); margin-right: 7px; vertical-align: middle;
  animation: lb-blink 2s ease-in-out infinite;
}
.lb-pulse.stale { background: var(--text-dim); animation: none; }
@keyframes lb-blink { 0%,100% { opacity: 1 } 50% { opacity: .25 } }
@media (prefers-reduced-motion: reduce) { .lb-pulse { animation: none } }

/* ── Standings table ────────────────────────────────────────────────────── */
.lb-card { padding: 0; overflow: hidden; }
.lb-scroll { overflow-x: auto; }
.lb-table { width: 100%; border-collapse: collapse; }
.lb-table th {
  font-size: clamp(10px, .95vw, 14px); letter-spacing: 1.2px;
  padding: 12px 10px; background: var(--paper-alt); white-space: nowrap;
}
.lb-table td { padding: 10px; border-bottom: 1px solid var(--paper-border); vertical-align: middle; }
.lb-table tr:last-child td { border-bottom: none; }
.lb-table tbody tr.lead { background: rgba(201, 162, 39, .17); }
.lb-table tbody tr.idle td { color: var(--ink-dim); }
/* Withdrawn: still on the board, visibly out of it. Wins on specificity over the to-par
   colours so a withdrawn player's -2 cannot read as a live red number. */
.lb-table tbody tr.wd td { color: var(--ink-dim); opacity: .62; }
.lb-wd {
  display: inline-block; margin-left: 8px; padding: 1px 7px; border-radius: 5px;
  border: 1px solid var(--ink-dim); color: var(--ink-dim);
  font-size: clamp(10px, .95vw, 14px); font-weight: 800; letter-spacing: .8px;
  vertical-align: middle;
}

/* Scoped through .lb-table so these beat theme.css's own bare th/td padding and alignment. */
.lb-table .lb-pos {
  width: 1%; white-space: nowrap; text-align: right;
  font-size: clamp(18px, 2.4vw, 40px); font-weight: 800; color: var(--ink);
  font-variant-numeric: tabular-nums; padding-right: 16px;
}
.lb-table th.lb-pos { font-size: clamp(10px, .95vw, 14px); }
.lb-table .lb-pos.none { color: var(--ink-dim); font-weight: 600; }

.lb-name { font-size: clamp(17px, 2.1vw, 34px); font-weight: 700; line-height: 1.15; }
.lb-slot {
  display: inline-block; margin-left: 8px; padding: 1px 7px; border-radius: 5px;
  background: var(--paper-alt); border: 1px solid var(--paper-border);
  color: var(--ink-dim); font-size: clamp(10px, .95vw, 15px);
  font-weight: 700; letter-spacing: .5px; vertical-align: middle;
}
.lb-players {
  margin-top: 3px; color: var(--ink-dim); font-weight: 500;
  font-size: clamp(12px, 1.1vw, 18px); line-height: 1.25;
}

.lb-table .lb-num {
  width: 1%; white-space: nowrap; text-align: right;
  font-variant-numeric: tabular-nums; font-weight: 700;
  font-size: clamp(17px, 2.1vw, 34px); padding-left: 18px;
}
.lb-table th.lb-num { font-size: clamp(10px, .95vw, 14px); }
.lb-topar { font-weight: 800; }
.lb-topar.under { color: var(--birdie); }
.lb-topar.over { color: var(--bogey); }
.lb-thru { font-weight: 600; color: var(--ink-dim); }
.lb-thru.done { color: var(--ink); }

/* Side-game money. Sized a step below the stroke-play figures on purpose — the event is
   the event, and this is what four of them have going among themselves.
   Scoped through .lb-table for the same reason .lb-num is: otherwise that rule's two
   classes win and the money reads at the same weight as the score. */
.lb-table .lb-money {
  font-size: clamp(14px, 1.7vw, 26px); font-weight: 800; color: var(--ink-dim);
}
.lb-table .lb-money.up { color: #1E7A3C; }
.lb-table .lb-money.down { color: var(--birdie); }
.lb-table .lb-money.none { font-weight: 500; }

/* ── Prize table ────────────────────────────────────────────────────────── */
.lb-section { margin-top: 28px; }
.lb-section h2 {
  font-size: clamp(14px, 1.3vw, 20px); text-transform: uppercase; letter-spacing: 1.4px;
  color: var(--text-dim); margin: 0 0 10px;
}
.lb-contest { display: flex; align-items: baseline; gap: 12px; padding: 10px 0; border-bottom: 1px solid var(--paper-border); }
.lb-contest:last-child { border-bottom: none; }
.lb-contest-kind {
  flex: none; min-width: 3.2em; text-align: center; padding: 2px 8px; border-radius: 6px;
  background: var(--ink); color: var(--paper); font-weight: 800;
  font-size: clamp(11px, 1.05vw, 16px); letter-spacing: .8px;
}
.lb-contest-what { flex: 1 1 auto; font-size: clamp(14px, 1.5vw, 22px); font-weight: 600; }
.lb-contest-hole { color: var(--ink-dim); font-weight: 500; }
.lb-contest-winner { flex: none; font-size: clamp(14px, 1.5vw, 22px); font-weight: 700; text-align: right; }
.lb-contest-winner.open { color: var(--ink-dim); font-weight: 500; font-style: italic; }

.lb-foot { margin-top: 14px; font-size: clamp(12px, 1.1vw, 16px); }
.lb-note { margin-top: 10px; font-size: clamp(11px, 1vw, 14px); }

/* ── Phone ──────────────────────────────────────────────────────────────── */
@media (max-width: 700px) {
  .lb-head { align-items: flex-start; }
  .lb-meta { text-align: left; }
  .lb-table th, .lb-table td { padding: 8px 6px; }
  .lb-pos { padding-right: 8px; }
}
@media (max-width: 520px) {
  .lb-hide-sm { display: none; }
}
`}</style>
  );
}

/**
 * One player's side-game money.
 *
 * `fmtMoney` always carries the sign — "+$6.00", "−$2.50", "—" for level — so the colour is
 * a second signal and never the only one. A player whose group registered no games has no
 * entry at all, which is a dash rather than a zero: they are not level, they are not in it.
 */
function MoneyCell({ cents }: { cents: number | undefined }) {
  if (cents === undefined) return <td className="lb-num lb-money none">–</td>;
  const tone = cents > 0 ? ' up' : cents < 0 ? ' down' : '';
  return <td className={`lb-num lb-money${tone}`}>{fmtMoney(cents)}</td>;
}

const toParClass = (row: LeaderboardRow) =>
  !row.started ? '' : row.toPar < 0 ? ' under' : row.toPar > 0 ? ' over' : '';

/** The board. Rows arrive already sorted and placed by the shared engine. */
export function Standings({
  rows,
  holeCount,
  scoring,
  money,
}: {
  rows: LeaderboardRow[];
  /** Holes in this round, so a completed card can read "F" instead of a number. */
  holeCount: number;
  /** The EFFECTIVE scoring — 'gross' when a net event could not be netted. */
  scoring: 'gross' | 'net';
  /**
   * Side-game winnings in cents, keyed by ENTRANT id — which is `row.id` in an individual
   * event. A separate lookup rather than a field on LeaderboardRow, because it is a
   * different game entirely: the stroke-play row is the whole field, this settles inside
   * one foursome, and folding them together would suggest the money is ranked too.
   *
   * Null (the default) hides the column outright. The host has to have turned it on.
   */
  money?: Record<string, number> | null;
}) {
  // A round with no pars recorded would otherwise mark every card "F" at thru 0.
  const finishedAt = holeCount > 0 ? holeCount : Number.POSITIVE_INFINITY;

  if (rows.length === 0) {
    return (
      <div className="card">
        <p style={{ margin: 0 }}>No foursomes have registered for this event yet.</p>
      </div>
    );
  }

  return (
    <div className="card lb-card">
      <div className="lb-scroll">
        <table className="lb-table">
          <thead>
            <tr>
              <th className="lb-pos" scope="col">
                Pos
              </th>
              <th scope="col">{scoring === 'net' ? 'Net standings' : 'Standings'}</th>
              <th className="lb-num" scope="col">
                To par
              </th>
              <th className="lb-num" scope="col">
                Thru
              </th>
              <th className="lb-num lb-hide-sm" scope="col">
                Total
              </th>
              {money ? (
                <th className="lb-num" scope="col">
                  Games
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const shown = scoring === 'net' && row.net !== null ? row.net : row.gross;
              // Withdrawn players are greyed, never dropped: their holes are on a card
              // their group played, and erasing the person erases the hole. The engine has
              // already unranked them and sorted them last; this only has to show it.
              const rowClass = row.withdrawn
                ? 'wd'
                : row.rank === 1
                  ? 'lead'
                  : !row.started
                    ? 'idle'
                    : undefined;
              return (
                <tr key={row.id} className={rowClass}>
                  {/* An empty position is the engine saying "not yet holed out anywhere". */}
                  <td className={row.position ? 'lb-pos' : 'lb-pos none'}>
                    {row.position || '–'}
                  </td>
                  <td>
                    <div className="lb-name">
                      {row.name}
                      {row.slotLabel ? <span className="lb-slot">{row.slotLabel}</span> : null}
                      {row.withdrawn ? <span className="lb-wd">WD</span> : null}
                    </div>
                    {row.subtitle ? <div className="lb-players">{row.subtitle}</div> : null}
                  </td>
                  <td className={`lb-num lb-topar${toParClass(row)}`}>
                    {row.started ? fmtToPar(row.toPar) : '–'}
                  </td>
                  {/* THRU is a count of completed holes — see the file header. */}
                  <td className={row.thru >= finishedAt ? 'lb-num lb-thru done' : 'lb-num lb-thru'}>
                    {!row.started ? '–' : row.thru >= finishedAt ? 'F' : row.thru}
                  </td>
                  <td className="lb-num lb-hide-sm">{row.started ? shown : '–'}</td>
                  {money ? <MoneyCell cents={money[row.id]} /> : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * The prize table.
 *
 * Unclaimed contests stay on the list on purpose — matching `contestStandings` in the
 * engine. What is still outstanding at the end of the day is the thing the host and the
 * field both want to see; a list that quietly omits it answers the wrong question.
 */
export function Contests({
  contests,
  holeNumberOffset,
}: {
  contests: ContestLine[];
  /** 9 on a back nine, so the signs on the tee boxes read 10–18. */
  holeNumberOffset: number;
}) {
  if (contests.length === 0) return null;

  const ordered = [...contests].sort((a, b) => a.holeIdx - b.holeIdx || a.kind.localeCompare(b.kind));

  return (
    <section className="lb-section">
      <h2>On the course</h2>
      <div className="card">
        {ordered.map((c, i) => {
          const kind = c.kind as ContestKind;
          const label = c.label?.trim() || CONTEST_LABELS[kind] || c.kind;
          const short = CONTEST_SHORT[kind] || '—';
          const holeNumber = c.holeIdx + 1 + holeNumberOffset;
          return (
            <div className="lb-contest" key={`${c.holeIdx}-${c.kind}-${i}`}>
              <span className="lb-contest-kind">{short}</span>
              <span className="lb-contest-what">
                {label} <span className="lb-contest-hole">— hole {holeNumber}</span>
              </span>
              <span className={c.winner ? 'lb-contest-winner' : 'lb-contest-winner open'}>
                {c.winner || 'unclaimed'}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default Standings;
